import { Bot, Building2, Copy, KeyRound, UserPlus } from "lucide-react";
import { TableSkeleton, useSlowEnoughToShow } from "../components/Skeleton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import type { AccountRecord, AdminUser, AgentKey, UserRole } from "../types";
import { formatCredits, formatDate } from "../utils";

/**
 * Super admin: clients, users and roles across every client.
 *
 * The one screen in the product that is not scoped to a single client, which is
 * why the server guards it separately. Hiding it from the nav is not the control —
 * the routes are.
 */

const ROLES: Array<{ value: UserRole; label: string; blurb: string }> = [
  { value: "client", label: "Client", blurb: "Sees their own workspace, submits requests" },
  { value: "account_manager", label: "Account manager", blurb: "Approves and completes work" },
  { value: "manager", label: "Manager", blurb: "Also creates credits and sets rates" },
  { value: "superadmin", label: "Super admin", blurb: "Also manages clients, users and roles" },
];

/** Shown once, then gone. The server never returns it again. */
function OneTimePassword({ email, password, invited }: { email: string; password: string; invited?: boolean }) {
  return (
    <div className="one-time-password" role="alert">
      <KeyRound size={18} />
      <div>
        <strong>Password for {email}</strong>
        <code>{password}</code>
        {/* `invited` is undefined for a password RESET, which sends no email — only
            a brand-new user gets an invite, so silence there is correct, not a gap. */}
        <span>
          {invited === true
            ? "Emailed to them with sign-in instructions. Keep this copy anyway until they are in — a bounce is silent."
            : invited === false
              ? "The invite email did NOT go out. Send this to them yourself."
              : "Shown once. They must change it at first sign-in, so send it however is convenient — it stops working the moment they use it."}
        </span>
      </div>
      <button
        type="button"
        className="button button-table"
        onClick={() => { void navigator.clipboard?.writeText(password); toast.success("Password copied"); }}
      >
        <Copy size={15} /> Copy
      </button>
    </div>
  );
}

export default function AdminPage() {
  const [clients, setClients] = useState<AccountRecord[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [issued, setIssued] = useState<{ email: string; password: string; invited?: boolean } | null>(null);

  const [client, setClient] = useState({ id: "", name: "", monthlyCredits: "69", creditValueKd: "50", contractStart: "", contractEnd: "" });
  const [user, setUser] = useState({ accountId: "", email: "", displayName: "", role: "client" as UserRole });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [clientData, userData] = await Promise.all([dashboardApi.clients(), dashboardApi.adminUsers()]);
      setClients(clientData.clients);
      setUsers(userData.users);
      setUser((current) => ({ ...current, accountId: current.accountId || clientData.clients[0]?.id || "" }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load administration.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const byClient = useMemo(() => {
    const groups = new Map<string, AdminUser[]>();
    for (const row of users) {
      const list = groups.get(row.account_name) || [];
      list.push(row);
      groups.set(row.account_name, list);
    }
    return [...groups.entries()];
  }, [users]);

  async function addClient(event: React.FormEvent) {
    event.preventDefault();
    try {
      await dashboardApi.createClient({
        id: client.id.trim().toLowerCase(),
        name: client.name.trim(),
        monthlyCredits: Number(client.monthlyCredits),
        creditValueKd: Number(client.creditValueKd),
        contractStart: client.contractStart,
        contractEnd: client.contractEnd,
      });
      toast.success(`${client.name} added`);
      setClient({ id: "", name: "", monthlyCredits: "69", creditValueKd: "50", contractStart: "", contractEnd: "" });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not add the client.");
    }
  }

  async function addUser(event: React.FormEvent) {
    event.preventDefault();
    try {
      const result = await dashboardApi.createUser({
        accountId: user.accountId,
        email: user.email.trim().toLowerCase(),
        displayName: user.displayName.trim(),
        role: user.role,
      });
      setIssued({ email: result.email, password: result.password, invited: result.invited });
      setUser({ accountId: user.accountId, email: "", displayName: "", role: "client" });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not add the user.");
    }
  }

  async function change(target: AdminUser, patch: { role?: UserRole; active?: boolean; resetPassword?: boolean }) {
    try {
      const result = await dashboardApi.updateUser(target.id, patch);
      if (result.password) setIssued({ email: target.email, password: result.password });
      else toast.success(`${target.display_name} updated`);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update the user.");
    }
  }

  const showSkeleton = useSlowEnoughToShow(loading);

  if (loading) return showSkeleton
    ? <div className="page-stack"><TableSkeleton rows={4} label="Loading clients and people" /></div>
    : null;

  return (
    <div className="page-stack">
      {issued && <OneTimePassword email={issued.email} password={issued.password} invited={issued.invited} />}

      <section className="panel">
        <div className="panel-header compact">
          <div><span className="section-eyebrow">Clients</span><h3>{clients.length} on the system</h3></div>
        </div>
        <div className="responsive-table-wrap">
          <table className="data-table">
            <thead><tr><th>Client</th><th>Credits a month</th><th>Credit value</th><th>Contract</th><th>Users</th></tr></thead>
            <tbody>
              {clients.map((row) => (
                <tr key={row.id}>
                  <td data-label="Client"><strong>{row.name}</strong><span>{row.id}</span></td>
                  <td data-label="Credits a month">{formatCredits(row.monthly_credits)}</td>
                  {/* KD belongs here: this is where the commercial terms are set. */}
                  <td data-label="Credit value">{formatCredits(row.credit_value_kd)} KD</td>
                  <td data-label="Contract">{formatDate(row.contract_start)} – {formatDate(row.contract_end)}</td>
                  <td data-label="Users">{users.filter((u) => u.account_id === row.id).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <form className="admin-form" onSubmit={addClient}>
          <h4><Building2 size={17} /> Add a client</h4>
          <div className="admin-grid">
            <div className="form-field"><label htmlFor="c-name">Name</label><input id="c-name" required value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} placeholder="NBK" /></div>
            <div className="form-field"><label htmlFor="c-id">Short id</label><input id="c-id" required pattern="[a-z0-9-]+" value={client.id} onChange={(e) => setClient({ ...client, id: e.target.value })} placeholder="nbk" /><small>Lowercase, no spaces. Permanent.</small></div>
            <div className="form-field"><label htmlFor="c-credits">Credits a month</label><input id="c-credits" type="number" min="0" step="0.5" required value={client.monthlyCredits} onChange={(e) => setClient({ ...client, monthlyCredits: e.target.value })} /></div>
            <div className="form-field"><label htmlFor="c-value">KD per credit</label><input id="c-value" type="number" min="1" required value={client.creditValueKd} onChange={(e) => setClient({ ...client, creditValueKd: e.target.value })} /></div>
            <div className="form-field"><label htmlFor="c-start">Contract start</label><input id="c-start" type="date" required value={client.contractStart} onChange={(e) => setClient({ ...client, contractStart: e.target.value })} /></div>
            <div className="form-field"><label htmlFor="c-end">Contract end</label><input id="c-end" type="date" required value={client.contractEnd} onChange={(e) => setClient({ ...client, contractEnd: e.target.value })} /></div>
          </div>
          <button className="button button-primary" type="submit">Add client</button>
        </form>
      </section>

      <section className="panel">
        <div className="panel-header compact">
          <div><span className="section-eyebrow">People</span><h3>{users.length} users</h3></div>
        </div>

        {byClient.map(([name, rows]) => (
          <div key={name} className="admin-group">
            <h4>{name}</h4>
            <div className="responsive-table-wrap">
              <table className="data-table">
                <thead><tr><th>Person</th><th>Role</th><th>Clients</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td data-label="Person"><strong>{row.display_name}</strong><span>{row.email}</span></td>
                      <td data-label="Role">
                        <select value={row.role} onChange={(e) => void change(row, { role: e.target.value as UserRole })} aria-label={`Role for ${row.display_name}`}>
                          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </td>
                      {/* Only an account manager is scoped. A manager already reaches
                          every client and a client reaches only their own, so ticks
                          for them would be a control that changes nothing. */}
                      <td data-label="Clients">
                        {row.role === "account_manager"
                          ? <AssignedClients user={row} clients={clients} onSaved={load} />
                          : <span className="record-meta">{row.role === "client" ? "Own client only" : "All clients"}</span>}
                      </td>
                      <td data-label="Status">
                        {row.active ? (row.must_change_password ? <span className="status-pill pending">Password not set</span> : <span className="status-pill approved">Active</span>) : <span className="status-pill rejected">Inactive</span>}
                      </td>
                      <td data-label="Actions" className="admin-actions">
                        <button type="button" className="button button-table" onClick={() => void change(row, { resetPassword: true })}>Reset password</button>
                        <button type="button" className="button button-table" onClick={() => void change(row, { active: !row.active })}>{row.active ? "Deactivate" : "Reactivate"}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <form className="admin-form" onSubmit={addUser}>
          <h4><UserPlus size={17} /> Add a person</h4>
          <div className="admin-grid">
            <div className="form-field"><label htmlFor="u-client">Client</label><select id="u-client" required value={user.accountId} onChange={(e) => setUser({ ...user, accountId: e.target.value })}>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div className="form-field"><label htmlFor="u-name">Full name</label><input id="u-name" required value={user.displayName} onChange={(e) => setUser({ ...user, displayName: e.target.value })} /></div>
            <div className="form-field"><label htmlFor="u-email">Email</label><input id="u-email" type="email" required value={user.email} onChange={(e) => setUser({ ...user, email: e.target.value })} /><small>Unique across all clients.</small></div>
            <div className="form-field"><label htmlFor="u-role">Role</label><select id="u-role" value={user.role} onChange={(e) => setUser({ ...user, role: e.target.value as UserRole })}>{ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select><small>{ROLES.find((r) => r.value === user.role)?.blurb}</small></div>
          </div>
          <button className="button button-primary" type="submit">Add person</button>
        </form>
      </section>

      <AgentKeys people={users.filter((u) => u.active)} />
    </div>
  );
}

/**
 * Agent keys (2026-10-04, BM msgs 5133-5139). One per agent, so the trail says which
 * agent did what and one leaked key can be killed alone.
 *
 * An AGENT key acts as one person and can only read and raise requests. A PLATFORM
 * key (Prism) acts as whichever staff member is signed in there, with that person's
 * own rights. The key is shown once; the server keeps only a hash.
 */
function AgentKeys({ people }: { people: AdminUser[] }) {
  const [keys, setKeys] = useState<AgentKey[]>([]);
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null);
  const [form, setForm] = useState({ name: "", actsAsEmail: "", platform: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setKeys((await dashboardApi.agentKeys()).keys);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load agent keys.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await dashboardApi.createAgentKey({
        name: form.name.trim(),
        platform: form.platform,
        actsAsEmail: form.platform ? undefined : form.actsAsEmail,
      });
      setIssued({ name: form.name.trim(), key: result.key });
      setForm({ name: "", actsAsEmail: "", platform: false });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not make the key.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(key: AgentKey) {
    if (!window.confirm(`Kill the key "${key.name}"? The agent using it stops working at once.`)) return;
    try {
      await dashboardApi.revokeAgentKey(key.id);
      toast.success(`${key.name} key killed`);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not kill the key.");
    }
  }

  return (
    <section className="panel">
      <div className="panel-header compact">
        <div><span className="section-eyebrow">Agents</span><h3>{keys.filter((k) => !k.revokedAt).length} live keys</h3></div>
      </div>

      {issued && (
        <div className="one-time-password" role="alert">
          <KeyRound size={18} />
          <div>
            <strong>Key for {issued.name}</strong>
            <code>{issued.key}</code>
            <span>Shown once. Put it in the agent's settings now; it cannot be shown again. Setup guide: /agents.md</span>
          </div>
          <button
            type="button"
            className="button button-table"
            onClick={() => { void navigator.clipboard?.writeText(issued.key); toast.success("Key copied"); }}
          >
            <Copy size={15} /> Copy
          </button>
        </div>
      )}

      {keys.length ? (
        <div className="responsive-table-wrap">
          <table className="data-table">
            <thead><tr><th>Agent</th><th>Acts as</th><th>Last used</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id}>
                  <td data-label="Agent"><strong>{key.name}</strong><span>{key.prefix}…</span></td>
                  <td data-label="Acts as">
                    {key.platform
                      ? <span className="record-meta">Platform: whoever is signed in there</span>
                      : <span>{key.actsAs?.name ?? "—"}<br /><span className="record-meta">{key.actsAs?.email}</span></span>}
                  </td>
                  <td data-label="Last used">{key.lastUsedAt ? formatDate(key.lastUsedAt, true) : "Never"}</td>
                  <td data-label="Status">
                    {key.revokedAt
                      ? <span className="status-pill rejected">Killed {formatDate(key.revokedAt)}</span>
                      : <span className="status-pill approved">Live</span>}
                  </td>
                  <td data-label="Actions" className="admin-actions">
                    {!key.revokedAt && <button type="button" className="button button-table" onClick={() => void revoke(key)}>Kill key</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="record-meta">No agent keys yet.</p>}

      <form className="admin-form" onSubmit={create}>
        <h4><Bot size={17} /> Make an agent key</h4>
        <div className="admin-grid">
          <div className="form-field">
            <label htmlFor="k-name">Agent name</label>
            <input id="k-name" required minLength={2} maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ajeeb AM" />
            <small>Shown in the activity log: "raised by Fatma via Ajeeb AM".</small>
          </div>
          <div className="form-field">
            <label htmlFor="k-kind">Kind</label>
            <select id="k-kind" value={form.platform ? "platform" : "agent"} onChange={(e) => setForm({ ...form, platform: e.target.value === "platform" })}>
              <option value="agent">Agent (read + raise)</option>
              <option value="platform">Platform (Prism)</option>
            </select>
            <small>{form.platform
              ? "Acts for whoever is signed in there, with their own rights."
              : "Reads and raises requests. Cannot approve or add credits."}</small>
          </div>
          {!form.platform && (
            <div className="form-field">
              <label htmlFor="k-person">Acts as</label>
              <select id="k-person" required value={form.actsAsEmail} onChange={(e) => setForm({ ...form, actsAsEmail: e.target.value })}>
                <option value="">Pick a person</option>
                {people.map((p) => <option key={p.id} value={p.email}>{p.display_name} ({p.email})</option>)}
              </select>
              <small>The key sees exactly the clients this person sees.</small>
            </div>
          )}
        </div>
        <button className="button button-primary" type="submit" disabled={busy}>{busy ? "Making…" : "Make key"}</button>
      </form>
    </section>
  );
}


/**
 * Which clients one account manager may work in.
 *
 * The list is EDITED as a whole and SAVED as a whole, because the server replaces
 * rather than adds — an add-only save would leave a client the superadmin just
 * unticked still reachable, which is the failure that costs something here.
 *
 * The home client is shown but cannot be unticked: `users.account_id` is where the
 * person was created and is the scope of their session the moment they sign in.
 * Removing it would sign somebody in to a workspace they are not allowed in — a
 * state the rest of the product has no way to express. `accountsFor()` enforces
 * the same rule server-side; this only makes it visible.
 */
function AssignedClients({ user, clients, onSaved }: {
  user: AdminUser;
  /** The minimum this needs, so the page can pass the client rows it already
   *  loaded rather than fetching the same names a second time. */
  clients: Array<{ id: string; name: string }>;
  onSaved: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string[]>(user.assignedAccountIds);
  const [saving, setSaving] = useState(false);

  // Re-sync when the row is reloaded, or the panel shows what the admin typed
  // before a failed save rather than what is actually stored.
  useEffect(() => setChosen(user.assignedAccountIds), [user.assignedAccountIds]);

  const reach = new Set([user.account_id, ...chosen]);

  async function save() {
    setSaving(true);
    try {
      // The home client is never SENT: it is not a grant, it is where they live.
      // Storing it as one would make un-assigning it look possible.
      await dashboardApi.setUserAccounts(user.id, chosen.filter((id) => id !== user.account_id));
      toast.success(`${user.display_name} now works in ${reach.size} client(s).`);
      setOpen(false);
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save that.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="button button-table" onClick={() => setOpen(true)}>
        {reach.size} client{reach.size === 1 ? "" : "s"}
      </button>
    );
  }

  return (
    <div className="assign-panel">
      <ul>
        {clients.map((client) => {
          const home = client.id === user.account_id;
          return (
            <li key={client.id}>
              <label>
                <input
                  type="checkbox"
                  checked={home || chosen.includes(client.id)}
                  disabled={home || saving}
                  onChange={(event) => setChosen((was) =>
                    event.target.checked ? [...was, client.id] : was.filter((id) => id !== client.id),
                  )}
                />
                <span>{client.name}</span>
                {home && <em>home</em>}
              </label>
            </li>
          );
        })}
      </ul>
      <div className="assign-actions">
        <button type="button" className="button button-table" disabled={saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" className="button button-table" disabled={saving} onClick={() => { setChosen(user.assignedAccountIds); setOpen(false); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
